import { Button, StatusChip, type StatusTone } from "@prisma/ui";
import * as RadixDialog from "@radix-ui/react-dialog";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Ban, Download, FileText, Film, Image as ImageIcon, Music, ShieldAlert, X } from "lucide-react";
import { useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";

import { api, ApiError, call } from "../../lib/api";
import type { components } from "../../lib/api-types";
import { formatBytes, formatDate } from "../../lib/format";
import { useSession } from "../../lib/session";

export type AssetCard = components["schemas"]["AssetCardOut"];
type Detail = components["schemas"]["AssetDetailOut"];
type Board = components["schemas"]["ProductionBoardOut"];

const APPROVAL_TONE: Record<string, StatusTone> = {
  UNREVIEWED: "neutral", APPROVED: "success", REJECTED: "danger", RESTRICTED: "warning", ARCHIVED: "neutral",
};
const MEDIA_ICON = { VIDEO: Film, IMAGE: ImageIcon, AUDIO: Music, DOCUMENT: FileText } as const;
const ACT = { add: "addToJob", restrict: "restrict", archive: "archive" } as const;
const ADD_TO = ["todo", "in_progress", "revision"] as const;

export function ApprovalChip({ status }: { status: string }) {
  const { t } = useTranslation();
  return <StatusChip tone={APPROVAL_TONE[status] ?? "neutral"} icon={null} label={t(`assets.approval_${status.toLowerCase()}`)} />;
}

export function AssetTile({ a, onOpen }: { a: AssetCard; onOpen: (id: string) => void }) {
  const { t } = useTranslation();
  const Icon = MEDIA_ICON[a.media_type as keyof typeof MEDIA_ICON] ?? FileText;
  return (
    <li>
      <button type="button" onClick={() => onOpen(a.id)}
        className="block w-full overflow-hidden rounded-[var(--radius-card)] border border-line bg-surface text-left shadow-[var(--shadow-card)] hover:border-[var(--border-strong)]">
        <div className="relative flex aspect-[4/3] items-center justify-center bg-soft">
          {a.thumbnail_url ? (
            <img src={a.thumbnail_url} alt="" className="h-full w-full object-cover" loading="lazy" />
          ) : (
            <Icon aria-hidden="true" className="size-8 text-ink-2" strokeWidth={1.5} />
          )}
          <span className="absolute left-2 top-2 rounded-md bg-surface px-1.5 py-0.5 text-[11px] font-medium text-ink">{t(`assets.type_${a.asset_type.toLowerCase()}`)}</span>
          {a.processing_status !== "READY" && (
            <span className="absolute bottom-2 left-2 rounded-md bg-surface px-1.5 py-0.5 text-[11px] text-ink-2">{t(`assets.processing_${a.processing_status.toLowerCase()}`)}</span>
          )}
          {a.approval_status === "RESTRICTED" && (
            <span className="absolute right-2 top-2 rounded-full bg-surface p-1 text-[var(--status-warning)]"><ShieldAlert aria-hidden="true" className="size-4" /></span>
          )}
        </div>
        <div className="space-y-1 p-3 text-[13px]">
          <div className="flex items-center justify-between gap-2"><span className="font-semibold text-ink">{a.display_id}</span><ApprovalChip status={a.approval_status} /></div>
          <div className="truncate text-ink-2">{a.product.name}{a.job ? ` · ${a.job}` : ""}</div>
          <div className="truncate text-ink-2">{a.file_name} · {formatBytes(a.file_size)}</div>
        </div>
      </button>
    </li>
  );
}

function ErrorLine({ error }: { error: unknown }) {
  const { t } = useTranslation();
  if (!error) return null;
  const code = error instanceof ApiError ? error.code : "unknown";
  return <p role="alert" className="text-[13px] text-[var(--status-danger)]">{t(`error.${code}`, { defaultValue: t("error.unknown") })}</p>;
}

/** Asset detail drawer (doc 04 P26): ID, product, campaign, source job, parent and derived
 *  assets, usage, versions, download; reuse safety before Add to job. */
export function AssetDrawer({ id, onClose, onOpen }: { id: string | null; onClose: () => void; onOpen: (id: string) => void }) {
  const { t, i18n } = useTranslation();
  const me = useSession();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["asset", id], enabled: Boolean(id),
    queryFn: () => call<Detail>(api.GET("/app/v1/assets/{asset_id}", { params: { path: { asset_id: id ?? "" } } })) });
  const related = useQuery({ queryKey: ["asset", id, "related"], enabled: Boolean(id),
    queryFn: () => call<AssetCard[]>(api.GET("/app/v1/assets/{asset_id}/related", { params: { path: { asset_id: id ?? "" } } })) });
  const [mode, setMode] = useState<null | "restrict" | "archive" | "add">(null);
  const [reason, setReason] = useState("");
  const [job, setJob] = useState("");
  const jobs = useQuery({ queryKey: ["jobs", "for-asset"], enabled: mode === "add",
    queryFn: () => call<Board>(api.GET("/app/v1/production/board", { params: { query: {} } })) });
  const refresh = () => { void qc.invalidateQueries({ queryKey: ["asset", id] }); void qc.invalidateQueries({ queryKey: ["assets"] }); };
  const act = useMutation({
    mutationFn: (action: "restrict" | "archive") => call(api.POST("/app/v1/assets/{asset_id}/{action}", {
      params: { path: { asset_id: id ?? "", action } }, body: { reason: reason.trim(), row_version: q.data?.row_version ?? 0 } })),
    onSuccess: () => { setMode(null); setReason(""); refresh(); },
  });
  const use = useMutation({
    mutationFn: () => call(api.POST("/app/v1/assets/{asset_id}/use", { params: { path: { asset_id: id ?? "" } }, body: { job } })),
    onSuccess: () => { setMode(null); setJob(""); refresh(); },
  });
  const d = q.data;
  const can = (a: string) => Boolean(d?.allowed_actions.includes(a));
  const candidates = ADD_TO.flatMap((c) => jobs.data?.columns[c] ?? []);
  const row = (label: string, value: ReactNode) => (
    <div className="flex gap-3 py-1.5"><dt className="w-32 shrink-0 text-ink-2">{label}</dt><dd className="min-w-0 text-ink">{value ?? "—"}</dd></div>
  );
  return (
    <RadixDialog.Root open={Boolean(id)} onOpenChange={(o) => !o && onClose()}>
      <RadixDialog.Portal>
        <RadixDialog.Overlay className="fixed inset-0 z-40 bg-black/40" />
        <RadixDialog.Content aria-describedby={undefined}
          className="fixed inset-y-0 right-0 z-50 w-full max-w-[520px] overflow-y-auto border-l border-line bg-surface p-6 shadow-[var(--shadow-card)]">
          <div className="flex items-start justify-between gap-3">
            <RadixDialog.Title className="text-[20px] font-semibold text-ink">{d?.display_id ?? t("common.loading")}</RadixDialog.Title>
            <RadixDialog.Close aria-label={t("common.close")} className="rounded p-1 text-ink-2 hover:text-ink"><X aria-hidden="true" className="size-5" /></RadixDialog.Close>
          </div>
          {d && (
            <div className="mt-4 space-y-5">
              <div className="overflow-hidden rounded-[var(--radius-card)] bg-soft">
                {d.media.proxy && d.media_type === "VIDEO" ? (
                  <video src={d.media.proxy} poster={d.media.poster} controls className="aspect-video w-full bg-black" aria-label={t("assets.preview")} />
                ) : d.media.poster || d.thumbnail_url ? (
                  <img src={d.media.poster ?? d.thumbnail_url ?? ""} alt={t("assets.preview_of", { id: d.display_id })} className="max-h-80 w-full object-contain" />
                ) : (
                  <p className="p-8 text-center text-[13px] text-ink-2">{t(`assets.processing_${d.processing_status.toLowerCase()}`)}</p>
                )}
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <ApprovalChip status={d.approval_status} />
                <span className="text-[13px] text-ink-2">{t(`assets.type_${d.asset_type.toLowerCase()}`)} · {t(`assets.media_${d.media_type.toLowerCase()}`)}</span>
              </div>
              {d.reuse_blocked && (
                <p role="status" className="flex items-start gap-2 rounded-[var(--radius-control)] bg-[var(--status-warning-soft)] p-3 text-[13px] text-[var(--status-warning)]">
                  <Ban aria-hidden="true" className="mt-0.5 size-4 shrink-0" />{t("assets.reuse_blocked")}{d.restriction_reason ? ` ${d.restriction_reason}` : ""}
                </p>
              )}
              <dl className="divide-y divide-[var(--border-default)] text-[13px]">
                {row(t("requests.col_product"), d.product.name)}
                {row(t("requests.campaign"), d.campaign_id)}
                {row(t("assets.source_job"), d.job ? <Link className="text-violet-text underline" to={`/production/jobs/${d.job}`}>{d.job}</Link> : null)}
                {row(t("assets.request"), d.request ? <Link className="text-violet-text underline" to={`/requests/${d.request}`}>{d.request}</Link> : null)}
                {row(t("assets.consent"), d.consent_status ? t(`production.consent_option_${d.consent_status.toLowerCase()}`) : null)}
                {row(t("assets.file"), `${d.file_name} · ${formatBytes(d.file_size)}`)}
                {row(t("assets.dimensions"), d.width && d.height ? `${d.width}×${d.height}${d.duration_s ? ` · ${d.duration_s.toFixed(1)} s` : ""}` : null)}
                {row(t("assets.created"), d.created_at ? formatDate(d.created_at, i18n.language, me.time_zone) : null)}
              </dl>
              <section>
                <h3 className="text-[14px] font-semibold text-ink">{t("assets.lineage")}</h3>
                <ul className="mt-1 space-y-1 text-[13px]">
                  {d.parents.map((p) => <li key={p.id}><button type="button" className="text-violet-text underline" onClick={() => onOpen(p.id)}>{p.display_id}</button> · {t(`assets.rel_${p.relation.toLowerCase()}`)}</li>)}
                  {d.derived.map((p) => <li key={p.id}>{t("assets.derived")} <button type="button" className="text-violet-text underline" onClick={() => onOpen(p.id)}>{p.display_id}</button></li>)}
                  {d.parents.length + d.derived.length === 0 && <li className="text-ink-2">{t("assets.no_lineage")}</li>}
                </ul>
              </section>
              <section>
                <h3 className="text-[14px] font-semibold text-ink">{t("assets.usage")}</h3>
                <ul className="mt-1 space-y-1 text-[13px]">
                  {d.usage.map((u, n) => <li key={n}><Link className="text-violet-text underline" to={`/production/jobs/${u.job}`}>{u.job}</Link> · {t(`assets.usage_${u.usage_type.toLowerCase()}`)}</li>)}
                </ul>
              </section>
              <section>
                <h3 className="text-[14px] font-semibold text-ink">{t("assets.versions")}</h3>
                <ul className="mt-1 space-y-1 text-[13px] text-ink">
                  {d.versions.map((v) => <li key={v.version}>{t("plan.version_n", { n: v.version })} · {t(`assets.version_${v.status.toLowerCase()}`)} · {formatBytes(v.file_size)}</li>)}
                </ul>
              </section>
              <div className="flex flex-wrap gap-2">
                {d.media.original && (
                  <a href={d.media.original} download={d.file_name}
                    className="inline-flex h-10 items-center gap-2 rounded-[var(--radius-control)] border border-line bg-surface px-4 text-sm font-medium text-ink hover:bg-elevated">
                    <Download aria-hidden="true" className="size-4" />{t("assets.download_original")}
                  </a>
                )}
                {can(ACT.add) && <Button variant="primary" onClick={() => setMode("add")}>{t("assets.add_to_job")}</Button>}
                {can(ACT.restrict) && <Button variant="ghost" onClick={() => setMode("restrict")}>{t("assets.restrict")}</Button>}
                {can(ACT.archive) && <Button variant="ghost" onClick={() => setMode("archive")}>{t("assets.archive")}</Button>}
              </div>
              {mode === "add" && (
                <form className="space-y-2 rounded-[var(--radius-card)] border border-line p-4" onSubmit={(e) => { e.preventDefault(); if (job) use.mutate(); }}>
                  <p className="text-[13px] text-ink-2">{t("assets.add_check", { status: t(`assets.approval_${d.approval_status.toLowerCase()}`), product: d.product.name })}</p>
                  <label htmlFor="use-job" className="block text-[13px] font-medium text-ink-2">{t("assets.choose_job")}</label>
                  <select id="use-job" value={job} onChange={(e) => setJob(e.target.value)} className="h-10 w-full rounded-[var(--radius-control)] border border-line bg-surface px-3 text-[14px] text-ink">
                    <option value="">{t("assets.choose_job")}</option>
                    {candidates.map((j) => <option key={j.id} value={j.display_id}>{j.display_id} · {j.product.name}</option>)}
                  </select>
                  <ErrorLine error={use.error} />
                  <div className="flex justify-end gap-2"><Button variant="ghost" onClick={() => setMode(null)}>{t("common.cancel")}</Button><Button type="submit" variant="primary" disabled={!job || use.isPending}>{t("assets.add_to_job")}</Button></div>
                </form>
              )}
              {(mode === "restrict" || mode === "archive") && (
                <form className="space-y-2 rounded-[var(--radius-card)] border border-line p-4" onSubmit={(e) => { e.preventDefault(); if (reason.trim()) act.mutate(mode); }}>
                  <label htmlFor="asset-reason" className="block text-[13px] font-medium text-ink-2">{t(`assets.${mode}_reason`)}</label>
                  <textarea id="asset-reason" rows={3} value={reason} onChange={(e) => setReason(e.target.value)} className="w-full rounded-[var(--radius-control)] border border-line bg-surface p-3 text-[14px] text-ink" />
                  <ErrorLine error={act.error} />
                  <div className="flex justify-end gap-2"><Button variant="ghost" onClick={() => setMode(null)}>{t("common.cancel")}</Button><Button type="submit" variant="primary" disabled={!reason.trim() || act.isPending}>{t(`assets.${mode}`)}</Button></div>
                </form>
              )}
              <section>
                <h3 className="text-[14px] font-semibold text-ink">{t("assets.related")}</h3>
                {(related.data ?? []).length === 0 ? (
                  <p className="mt-1 text-[13px] text-ink-2">{t("assets.no_related")}</p>
                ) : (
                  <ul className="mt-2 grid grid-cols-3 gap-2">
                    {(related.data ?? []).slice(0, 6).map((r) => (
                      <li key={r.id}>
                        <button type="button" onClick={() => onOpen(r.id)} className="block w-full overflow-hidden rounded-[var(--radius-control)] border border-line text-left">
                          <div className="flex aspect-square items-center justify-center bg-soft">
                            {r.thumbnail_url ? <img src={r.thumbnail_url} alt="" className="h-full w-full object-cover" /> : <FileText aria-hidden="true" className="size-5 text-ink-2" />}
                          </div>
                          <div className="truncate px-1.5 py-1 text-[11px] text-ink">{r.display_id}</div>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            </div>
          )}
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
}
